/**
 * ProductShowcaseCard
 * -----------------------------------------------------------------------------
 * Presentational card used on `/platform/marketing` to showcase a SuprAI demo
 * product. Purely decorative — never wraps a link, never exposes a demo URL.
 *
 * Anatomy (top → bottom):
 *   1. Tilted screenshot stack (primary + two secondaries behind)
 *   2. Caption strip inside the primary card (product identifier only, no URL)
 *   3. Product name, tagline (violet), description
 *
 * Props
 *   primary:      { src, alt }                    — front-most screenshot
 *   secondaries:  Array<{ src, alt }>             — 0..2 layers behind
 *   label:        string                          — caption strip text (no URL)
 *   name:         string                          — product name (H3)
 *   tagline:      string                          — one-line tagline
 *   description:  string                          — 2–3 sentence body copy
 *   testId:       string                          — optional data-testid slug
 *
 * Behaviour
 *   Desktop / tablet: 3D tilt, scroll parallax on the primary, hover fan-out.
 *   Mobile (<768px):  flat primary + one secondary, no tilt, no parallax.
 *   Reduced-motion:   flat, no tilt, no parallax, no hover transforms.
 */

import React, { useRef, useState } from "react";
import {
  motion,
  useReducedMotion,
  useScroll,
  useTransform,
} from "framer-motion";

import { useViewportTier } from "@/components/scenes/hooks/useViewportTier";

const RIM_IDLE =
  "0 0 0 1px rgba(114,0,219,0.35), 0 24px 60px -12px rgba(114,0,219,0.35), 0 8px 24px rgba(2,4,11,0.6)";
const RIM_HOVER =
  "0 0 0 1px rgba(114,0,219,0.55), 0 32px 80px -12px rgba(114,0,219,0.50), 0 12px 32px rgba(2,4,11,0.70)";
const RIM_FLAT =
  "0 0 0 1px rgba(114,0,219,0.30), 0 12px 32px rgba(2,4,11,0.55)";

export function ProductShowcaseCard({
  primary,
  secondaries = [],
  label,
  name,
  tagline,
  description,
  testId,
}) {
  const cardRef = useRef(null);
  const reducedMotion = useReducedMotion();
  const tier = useViewportTier();
  const [hovered, setHovered] = useState(false);

  const isMobile = tier === "mobile";
  const animate = !reducedMotion && !isMobile;

  // Scroll parallax — subtle vertical translation as the card passes through
  // the viewport. Runs on tablet + desktop only.
  const { scrollYProgress } = useScroll({
    target: cardRef,
    offset: ["start end", "end start"],
  });
  const yPrimary = useTransform(
    scrollYProgress,
    [0, 1],
    animate ? [16, -16] : [0, 0],
  );
  const ySecondary = useTransform(
    scrollYProgress,
    [0, 1],
    animate ? [-8, 8] : [0, 0],
  );

  // Idle tilt / offsets — swapped out for the flat variant when animate=false.
  const primaryTransform = animate
    ? "perspective(1200px) rotateX(6deg) rotateY(-4deg)"
    : "perspective(1200px) rotateX(0) rotateY(0)";

  // Secondaries only render if provided; mobile drops the deepest layer.
  const visibleSecondaries = isMobile
    ? secondaries.slice(0, 1)
    : secondaries.slice(0, 2);

  const secondaryStyles = [
    // secondaries[0] — mid layer
    {
      translateY: -16,
      translateX: 20,
      scale: 0.94,
      opacity: 0.7,
      filter: "blur(0.5px) saturate(0.85)",
      hoverTranslateX: 32,
      hoverOpacity: 0.85,
    },
    // secondaries[1] — deepest layer
    {
      translateY: -32,
      translateX: 40,
      scale: 0.88,
      opacity: 0.4,
      filter: "blur(1.2px) saturate(0.75)",
      hoverTranslateX: 52,
      hoverOpacity: 0.55,
    },
  ];

  return (
    <div
      ref={cardRef}
      data-testid={testId}
      onMouseEnter={() => animate && setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: "rgba(21,10,36,0.60)",
        backdropFilter: "blur(20px)",
        WebkitBackdropFilter: "blur(20px)",
        border: `1px solid ${
          hovered ? "var(--border-active)" : "var(--border-hairline)"
        }`,
        borderRadius: "var(--radius-lg)",
        padding: "var(--space-6)",
        transform: hovered ? "translateY(-4px)" : "translateY(0)",
        transition:
          "transform var(--duration-base) var(--ease-out), border-color var(--duration-base) var(--ease-out)",
      }}
    >
      {/* Mobile-only: caption strip renders ABOVE the screenshot as a full-width label */}
      {isMobile && (
        <div
          aria-hidden="true"
          className="mb-4 inline-block rounded-sm px-3 py-1"
          style={{
            background: "rgba(2,4,11,0.72)",
            border: "1px solid var(--border-hairline)",
            color: "var(--frac-violet)",
            fontSize: "12px",
            fontWeight: 600,
            letterSpacing: "var(--tracking-caps)",
            textTransform: "uppercase",
          }}
        >
          {label}
        </div>
      )}

      {/* Screenshot stack — aspect-ratio wrapper keeps 16:10 (matches 1440×900 shots) */}
      <div
        className="relative w-full"
        style={{
          aspectRatio: "16 / 10",
          perspective: animate ? "1200px" : undefined,
        }}
      >
        {/* Secondaries — back-to-front so deepest paints first. */}
        {visibleSecondaries.map((sec, i) => {
          // sec at position i corresponds to secondaryStyles[i]; i=0 is mid, i=1 is deepest.
          // Paint order: render deepest last-in-DOM before primary so primary is on top;
          // we render deepest last via reverse for correct visual stacking below primary.
          return null; // placeholder — we render them below in reverse order.
        })}
        {[...visibleSecondaries]
          .map((sec, i) => ({ sec, i }))
          .reverse()
          .map(({ sec, i }) => {
            const s = secondaryStyles[i];
            const currentTranslateX = hovered ? s.hoverTranslateX : s.translateX;
            const currentOpacity = hovered ? s.hoverOpacity : s.opacity;
            return (
              <motion.img
                key={`sec-${i}`}
                src={sec.src}
                alt={sec.alt}
                loading="lazy"
                decoding="async"
                style={{
                  position: "absolute",
                  inset: 0,
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  borderRadius: "var(--radius-md)",
                  transform: `translateY(${s.translateY}px) translateX(${currentTranslateX}px) scale(${s.scale})`,
                  opacity: currentOpacity,
                  filter: animate ? s.filter : "none",
                  boxShadow:
                    "0 8px 24px rgba(2,4,11,0.55), 0 0 0 1px rgba(237,241,245,0.04)",
                  transition:
                    "transform var(--duration-base) var(--ease-out), opacity var(--duration-base) var(--ease-out)",
                  y: ySecondary,
                  zIndex: 1,
                }}
              />
            );
          })}

        {/* Primary — the tilted front card */}
        <motion.img
          src={primary.src}
          alt={primary.alt}
          loading="lazy"
          decoding="async"
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            borderRadius: "var(--radius-md)",
            transform: primaryTransform,
            transformOrigin: "center center",
            boxShadow: animate
              ? hovered
                ? RIM_HOVER
                : RIM_IDLE
              : RIM_FLAT,
            transition:
              "transform var(--duration-base) var(--ease-out), box-shadow var(--duration-base) var(--ease-out)",
            y: yPrimary,
            zIndex: 2,
          }}
        />

        {/* Caption strip — desktop / tablet only, overlaid on primary bottom-left.
            Decorative product identifier — NOT a URL. aria-hidden per brief. */}
        {!isMobile && (
          <div
            aria-hidden="true"
            style={{
              position: "absolute",
              left: "16px",
              bottom: "16px",
              zIndex: 3,
              background: "rgba(2,4,11,0.72)",
              backdropFilter: "blur(12px)",
              WebkitBackdropFilter: "blur(12px)",
              borderRadius: "var(--radius-sm)",
              padding: "4px 12px",
              color: "var(--frac-violet)",
              fontSize: "12px",
              fontWeight: 600,
              letterSpacing: "var(--tracking-caps)",
              textTransform: "uppercase",
              border: "1px solid rgba(114,0,219,0.35)",
            }}
          >
            {label}
          </div>
        )}
      </div>

      {/* Text block */}
      <div className="mt-8">
        <h3
          className="font-bold text-ink-primary"
          style={{ fontSize: "25px", lineHeight: "1.3", fontWeight: 700 }}
        >
          {name}
        </h3>
        <p
          className="mt-2 font-semibold"
          style={{
            color: "var(--frac-violet)",
            fontSize: "16px",
            fontWeight: 600,
          }}
        >
          {tagline}
        </p>
        <p
          className="mt-3 max-w-prose text-base leading-normal text-ink-secondary"
          style={{ fontWeight: 400 }}
        >
          {description}
        </p>
      </div>
    </div>
  );
}

export default ProductShowcaseCard;
